import { Col, Form, Input, InputNumber, Row, Spin } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import { CampaignBean } from 'common/api/api.ts';
import { advertiserApi, campaignApi } from 'common/api/entitiesApi.tsx';
import { AnchorsForm } from 'common/components/AnchorsForm.tsx';
import { ButtonsPanel } from 'common/components/ButtonsPanel.tsx';
import { TimeZoneSelector } from 'common/components/dictionary/TimeZoneSelector';
import { FormCard } from 'common/components/FormCard.tsx';
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector';
import { StartEndDates } from 'common/components/StartEndDates.tsx';
import { StatusSwitch } from 'common/components/StatusSwitch.tsx';
import { useAppContext } from 'common/context/AppContext.tsx';
import { useUserContext } from 'common/context/UserContext.tsx';
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import { apiCall, FormMode, getCopyName, showError } from 'common/util/Util.tsx';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CommonRoutes } from 'common/context/commonRoutes.ts'

const CurrencyEnum = CampaignBean.CurrencyEnum

export const CampaignForm = ({mode, agency}) => {
    const [loading, setLoading] = useState(false)
    const [form] = Form.useForm()
    const [entity, setEntity] = useState<CampaignBean>({
        active: true,
        ...(mode === FormMode.create && {startDate: dayjs().format('YYYY-MM-DD')})
    } as CampaignBean)
    const { user } = useUserContext()
    const advertiserId = Form.useWatch('advertiserId', form)

    const navigateTo = useNavigate()
    const {id} = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0
    const {
      parentAdvertiserId,
      setParentAdvertiserId,
      backToAdGroupId,
      setBackToAdGroupId,
      setBackToAdGroupMode,
      backToAdGroupMode,
      setCampaignIdToLink
    } = useAppContext()
    const appContext = useAppContext()

    const leaveForm = (response?: any) => {
        if (backToAdGroupId !== undefined) {
            setCampaignIdToLink(response?.data?.id)
            navigateTo(backToAdGroupMode === FormMode.create ? CommonRoutes.AD_GROUP_CREATE : `/ad-group/${backToAdGroupId}/${backToAdGroupMode}`)
        } else {
            navigateTo(CommonRoutes.CAMPAIGN)
        }
    }

    const deleteCampaign = () => {
        apiCall(
            setLoading,
            appContext,
            () => campaignApi.campaignDelete(idNum),
          () => leaveForm()
        )
    }

    const handleSubmit = () => {
      form.validateFields().then((values) => {
        const newEntity = { ...entity, ...values }

        const request =
          mode === 'edit' && id ? campaignApi.campaignUpdate(newEntity, idNum) : campaignApi.campaignCreate(newEntity)

        setLoading(true)
        request
          .then((response) => {
            queueListRepositionOnModifiedChange({
              appContext,
              routeKey: CommonRoutes.CAMPAIGN,
              previousEntity: entity,
              responseEntity: response?.data,
              enabled: mode === FormMode.edit && backToAdGroupId === undefined
            })
            leaveForm(response)
          })
          .catch((e) => {
            setLoading(false)
            showError(appContext, e)
          })
      })
    }

    useEffect(() => {
        if (mode == 'create') {
            if (advertiserId !== undefined) {
                apiCall(
                    setLoading,
                    appContext,
                    () => advertiserApi.adv(advertiserId),
                    (data) => {
                        if (data && data.data) {
                          form.setFieldValue('timezone', data.data.timezoneId)
                        }
                    },
                )
            } else if (user?.advertiserId !== undefined) {
                form.setFieldValue('timezone', user.timezoneId)
            }
        }
    }, [advertiserId, user?.advertiserId])

    useEffect(() => {
        if (mode === 'edit' || mode === 'copy') {
            apiCall(
                setLoading,
                appContext,
                () => campaignApi.campaign(idNum),
                (data) => {
                    if (data && data.data) {
                        const entityData = mode === 'copy' ? {...data.data, name: getCopyName(data.data.name)} : data.data
                        setEntity(entityData)
                        form.setFieldsValue(entityData)
                    }
                }
            )
        }
    }, [mode, id])

    useEffect(() => {
      return () => {
        setParentAdvertiserId(undefined);
        setBackToAdGroupId(undefined);
        setBackToAdGroupMode(undefined);
        setCampaignIdToLink(undefined);
      };
    }, [setParentAdvertiserId, setBackToAdGroupId, setBackToAdGroupMode, setCampaignIdToLink]);

    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={handleSubmit}>
                <FormCard
                    title="Campaign"
                    extra={
                        <StatusSwitch
                            entity={entity}
                            setEntity={setEntity}
                            tooltip="Control whether the campaign is active and allowed to run."
                        />
                    }
                >
                    {agency && (
                        <FormItem
                            required={true}
                            label="Advertiser"
                            tooltip="Select the advertiser who runs this campaign."
                            name="advertiserId"
                            initialValue={parentAdvertiserId}
                            rules={[{required: true, message: 'Advertiser is required'}]}
                        >
                            <AdvertiserSelector disabled={mode === 'edit' || !!parentAdvertiserId}/>
                        </FormItem>
                    )}

                    <FormItem
                        required
                        name="name"
                        label="Name"
                        rules={[{required: true, message: 'Please input Campaign Name'}]}
                    >
                        <Input/>
                    </FormItem>

                    <FormItem name="timezone" label="Time Zone"
                              tooltip="Define the time zone used for campaign scheduling."
                              required rules={[{required: true, message: 'Please specify Time Zone'}]}>
                        <TimeZoneSelector disabled={mode === 'edit' || (advertiserId === undefined && user?.advertiserId === undefined)}/>
                    </FormItem>

                    <StartEndDates
                        entity={entity}
                        setEntity={(e) => setEntity(e)}
                        form={form}
                        tooltip="Set the campaign start and end dates."
                    />

                    <FormItem label="Budget" tooltip="Set the maximum amount that can be spent on this campaign." required>
                        <Row gutter={8}>
                            <Col>
                                <FormItem
                                    name="totalBudget"
                                    noStyle
                                    required
                                    rules={[{required: true, message: 'Please input Budget'}]}
                                >
                                    <InputNumber
                                        step={0.01}
                                        min={0}
                                        style={{width: 200}}
                                        placeholder="Budget"
                                        className="subtle-placeholder"
                                    />
                                </FormItem>
                            </Col>
                            <Col>
                <span
                    style={{
                        display: 'inline-block',
                        height: '32px',
                        lineHeight: '32px'
                    }}
                >
                  {form.getFieldValue('currency') === CurrencyEnum.EUR ? '€' : '$'}
                </span>
                            </Col>
                        </Row>
                    </FormItem>
                </FormCard>

                <ButtonsPanel onCancel={leaveForm} showDelete={mode === 'edit'}
                              onDelete={() => deleteCampaign()}></ButtonsPanel>
            </AnchorsForm>
        </Spin>
    )
}
